import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { maskPhoneBR } from "@/lib/phone";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  HeartHandshake, Plus, ChevronUp, ChevronDown, Pencil, Trash2, ArrowLeft, Phone,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", relation: "" };

export default function SupportNetwork() {
  const nav = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);

  const load = () => api.get("/support-network").then((r) => setContacts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setConsent(false); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, phone: c.phone_display || "", relation: c.relation || "" }); setConsent(true); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome."); return; }
    if (!editing && !consent) { toast.error("É necessário confirmar o consentimento."); return; }
    try {
      if (editing) {
        await api.put(`/support-network/${editing.id}`, { ...form, consent: true });
        toast.success("Contato atualizado.");
      } else {
        await api.post("/support-network", { ...form, consent: true });
        toast.success("Pessoa adicionada à sua rede de apoio.");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/support-network/${id}`); toast.success("Contato removido."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= contacts.length) return;
    const arr = [...contacts];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setContacts(arr);
    try { await api.put("/support-network/reorder", { ids: arr.map((c) => c.id) }); }
    catch { load(); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-up">
      <button onClick={() => nav("/")} className="text-sm text-[#0F766E] flex items-center gap-1" data-testid="network-back"><ArrowLeft size={15} /> Início</button>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#34D399]/20 grid place-items-center shrink-0"><HeartHandshake size={24} className="text-[#0F766E]" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#1F3B4D]">Minha rede de apoio</h1>
            <p className="text-sm text-[#94A3B8]">Até 5 pessoas, na sua ordem de prioridade.</p>
          </div>
        </div>
        <Button onClick={openNew} disabled={contacts.length >= 5} data-testid="add-contact-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Plus size={16} /> Adicionar</Button>
      </div>

      <div className="space-y-3">
        {contacts.map((c, i) => (
          <Card key={c.id} data-testid={`contact-${c.id}`} className="rounded-2xl p-4 border-[#E0F2FE] flex items-center gap-3">
            <Badge className="bg-[#0F766E]/10 text-[#0F766E] border-0 w-7 h-7 grid place-items-center rounded-full p-0 shrink-0">{i + 1}</Badge>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1F3B4D]">{c.name}{c.relation ? <span className="text-[#94A3B8] font-normal"> · {c.relation}</span> : null}</div>
              <div className="text-sm text-[#94A3B8] flex items-center gap-1"><Phone size={12} /> {c.phone_display || c.phone}</div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`move-up-${c.id}`}><ChevronUp size={16} /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === contacts.length - 1} data-testid={`move-down-${c.id}`}><ChevronDown size={16} /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}><Pencil size={15} className="text-[#60A5FA]" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(c.id)} data-testid={`delete-${c.id}`}><Trash2 size={15} className="text-[#EF4444]" /></Button>
            </div>
          </Card>
        ))}
        {contacts.length === 0 && (
          <Card className="rounded-2xl p-8 text-center border-[#E0F2FE]">
            <p className="text-sm text-[#94A3B8]">Nenhuma pessoa cadastrada ainda. Adicione quem você gostaria de poder chamar em momentos difíceis.</p>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-[#1F3B4D]">{editing ? "Editar contato" : "Nova pessoa"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className="rounded-xl border-[#E0F2FE]" placeholder="Ex.: Maria" /></div>
            <div className="space-y-1"><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhoneBR(e.target.value) })} data-testid="contact-phone-input" className="rounded-xl border-[#E0F2FE]" placeholder="(11) 99999-9999" inputMode="numeric" /></div>
            <div className="space-y-1"><Label>Relação com você <span className="text-[#94A3B8] font-normal">(opcional)</span></Label><Input value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })} data-testid="contact-relation-input" className="rounded-xl border-[#E0F2FE]" placeholder="mãe, irmão, amigo…" /></div>
            {!editing && (
              <label className="flex items-start gap-2 text-sm text-[#1F3B4D] cursor-pointer rounded-xl bg-[#E0F2FE]/50 p-3">
                <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="contact-consent-checkbox" className="mt-0.5" />
                <span>Em situações em que você pedir ajuda, o Compasso Cuidar poderá enviar uma mensagem para este contato em seu nome.</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-contact-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
